/* eslint-disable sonarjs/no-nested-template-literals */
import { getLocalesFromIsoLocale, IsoLocale, Locales, TicketingFormCategory, UsaIsoLocale } from "@simplyk/common";
import { useRouter } from "next/router";

import i18n from "../../../config/i18n";
import { TicketingFrontendPageOutput } from "../../../types/trpc";

import { useTicketingTranslationField } from "@/hooks/useDonationTranslation";

const getEventHeaderMeta = ({
  locale,
  title,
  orgName,
  address,
  startDate,
}: {
  locale: Locales;
  title: string | undefined;
  orgName: string;
  address?: string;
  startDate?: string;
}): {
  metaTitle: string;
  metaDescription: string;
} => {
  switch (locale) {
    case Locales.EN:
      return {
        metaTitle: `${title} - Charity event${address ? ` at ${address}` : ""} | ${orgName} | Zeffy`,
        metaDescription: `Join us for ${title}, a fundraising event hosted by ${orgName}${startDate ? ` on ${startDate}` : ""}${address ? ` at ${address}` : ""}. Buy your ticket with Zeffy — 100% of proceeds go to ${orgName}.`,
      };
    case Locales.FR:
      return {
        metaTitle: `${title} - Événement caritatif${address ? ` à ${address}` : ""} | ${orgName} | Zeffy`,
        metaDescription: `Rejoignez-nous pour ${title}, un événement de collecte de fonds organisé par ${orgName}${startDate ? ` le ${startDate}` : ""}${address ? ` à ${address}` : ""}. Achetez votre billet avec Zeffy — 100 % des bénéfices seront reversées à ${orgName}.`,
      };
    case Locales.ES:
      return {
        metaTitle: `${title} - Evento benéfico${address ? ` en ${address}` : ""} | ${orgName} | Zeffy`,
        metaDescription: `Únete a nosotros en ${title}, un evento benéfico organizado por ${orgName}${startDate ? ` el ${startDate}` : ""}${address ? ` en ${address}` : ""}. Compra tu entrada con Zeffy - 100% de las ganancias van a ${orgName}.`,
      };
  }
};

const getMembershipHeaderMeta = ({
  locale,
  title,
  orgName,
}: {
  locale: Locales;
  title: string | undefined;
  orgName: string;
}): {
  metaTitle: string;
  metaDescription: string;
} => {
  switch (locale) {
    case Locales.EN:
      return {
        metaTitle: `${title} | ${orgName} | Zeffy`,
        metaDescription: `Make a payment to ${title}. Zeffy is 100% free - 100% of your payment goes directly to ${orgName}.`,
      };
    case Locales.FR:
      return {
        metaTitle: `${title} | ${orgName} | Zeffy`,
        metaDescription: `Effectuez un paiement à ${title}. Zeffy est 100 % gratuit  - 100 % de votre paiement est reversé directement à ${orgName}.`,
      };
    case Locales.ES:
      return {
        metaTitle: `${title} | ${orgName} | Zeffy`,
        metaDescription: `Realiza un pago a ${title}. Zeffy es 100 % gratuito: el 100 % de tu pago se destina directamente a ${orgName}.`,
      };
  }
};

const getLotteryHeaderMeta = ({
  locale,
  title,
  orgName,
}: {
  locale: Locales;
  title: string | undefined;
  orgName: string;
}): {
  metaTitle: string;
  metaDescription: string;
} => {
  switch (locale) {
    case Locales.EN:
      return {
        metaTitle: `${title} | ${orgName} | Zeffy`,
        metaDescription: `Enter ${orgName}'s fundraising raffle to support their mission. Raffle tickets available on Zeffy — 100% of proceeds go to the nonprofit.`,
      };
    case Locales.FR:
      return {
        metaTitle: `${title} | ${orgName} | Zeffy`,
        metaDescription: `Participez à la tombola de ${orgName} pour soutenir sa mission. Billets disponibles sur Zeffy — 100 % des bénéfices reversés à l'association.`,
      };
    case Locales.ES:
      return {
        metaTitle: `${title} | ${orgName} | Zeffy`,
        metaDescription: `Participa en la rifa de recaudación de fondos de ${orgName} para apoyar su misión. Boletos de la rifa disponibles en Zeffy - el 100 % de las ganancias se destina a la organización sin fines de lucro.`,
      };
  }
};

const getAuctionHeaderMeta = ({
  locale,
  orgName,
}: {
  locale: Locales;
  orgName: string;
}): {
  metaTitle: string;
  metaDescription: string;
} => {
  switch (locale) {
    case Locales.EN:
      return {
        metaTitle: `Silent Auction – ${orgName} | Zeffy`,
        metaDescription: `Bid to support ${orgName}'s silent auction. Hosted on Zeffy — the only 100% free silent auction platform for nonprofits.`,
      };
    case Locales.FR:
      return {
        metaTitle: `Vente aux enchères silencieuse – ${orgName} | Zeffy`,
        metaDescription: `Enchérissez pour soutenir la vente aux enchères silencieuse de ${orgName}. Hébergée sur Zeffy, la seule plateforme de vente aux enchères silencieuse 100 % gratuite pour les associations.`,
      };
    case Locales.ES:
      return {
        metaTitle: `Subasta silenciosa – ${orgName} | Zeffy`,
        metaDescription: `Puja para apoyar la subasta silenciosa de ${orgName}. Organizada por Zeffy, la única plataforma de subastas silenciosas 100 % gratuita para organizaciones sin fines de lucro.`,
      };
  }
};

const getShopHeaderMeta = ({
  locale,
  orgName,
}: {
  locale: Locales;
  orgName: string;
}): {
  metaTitle: string;
  metaDescription: string;
} => {
  switch (locale) {
    case Locales.EN:
      return {
        metaTitle: `${orgName}'s Online Store | Zeffy`,
        metaDescription: `Browse ${orgName}'s online store and support their mission with every purchase. Powered by Zeffy — 100% of proceeds go to the nonprofit.`,
      };
    case Locales.FR:
      return {
        metaTitle: `Boutique en ligne de ${orgName} | Zeffy`,
        metaDescription: `Parcourez la boutique en ligne de ${orgName} et soutenez sa mission à chaque achat. Propulsé par Zeffy — 100 % des bénéfices reversés à l'association.`,
      };
    case Locales.ES:
      return {
        metaTitle: `Tienda en línea de ${orgName} | Zeffy`,
        metaDescription: `Explora la tienda en línea de ${orgName} y apoya su misión con cada compra. Impulsado por Zeffy - el 100 % de las ganancias se destina a la organización sin fines de lucro.`,
      };
  }
};

export const useTicketingHeadMetaContent = ({
  ticketing,
  startDate,
}: {
  ticketing: TicketingFrontendPageOutput;
  startDate: string | undefined;
}): {
  metaTitle: string;
  metaDescription: string;
} => {
  const router = useRouter();
  const { ticketingField } = useTicketingTranslationField(ticketing as TicketingFrontendPageOutput);

  const isoLocale = (i18n?.language || router.locale || UsaIsoLocale.EN) as IsoLocale;
  const locale = getLocalesFromIsoLocale(isoLocale);

  const title = ticketingField?.title;

  switch (ticketing.formCategory) {
    case TicketingFormCategory.Event:
    case TicketingFormCategory.Custom:
      return getEventHeaderMeta({
        locale,
        title,
        orgName: ticketing.organization?.name,
        address: ticketing?.address || undefined,
        startDate,
      });
    case TicketingFormCategory.MembershipV2:
      return getMembershipHeaderMeta({
        locale,
        title,
        orgName: ticketing.organization?.name,
      });
    case TicketingFormCategory.Lottery:
      return getLotteryHeaderMeta({
        locale,
        title,
        orgName: ticketing.organization?.name,
      });
    case TicketingFormCategory.Auction:
      return getAuctionHeaderMeta({
        locale,
        orgName: ticketing.organization?.name,
      });
    case TicketingFormCategory.Shop:
      return getShopHeaderMeta({
        locale,
        orgName: ticketing.organization?.name,
      });
  }
};
